/**
 * Chat lifecycle — Single Source of Truth for BOTH tiers.
 *
 * WHY THIS FILE SITS OUTSIDE src/ AND server/
 * -------------------------------------------
 * The rules below started in src/utils/chatLifecycle.ts, written after the 2026-08-12
 * incident in which the AC badge said 14 open chats and the visible tabs summed to 13.
 * That file fixed the drift it could reach — the frontend. It could not reach the
 * server, which answers the same question in its own route handlers, so the server
 * kept its own copies. By 2026-08-17 the rule was re-implemented inline in eight
 * places across both tiers, and two of them had already drifted apart again:
 *
 *   · LayoutManager decided EXISTENCE with Rule 3 (skip liveness==='stuck'), which is
 *     exactly the hiding that Rule 3's own documentation forbids.
 *   · The /conversations endpoint returned the full history and left "what is open"
 *     to every caller, which is how an archive of 600+ records kept getting reported
 *     as live work.
 *
 * A rule that two tiers must agree on cannot live inside one of them. It lives here,
 * and both import it. src/utils/chatLifecycle.ts re-exports it so existing frontend
 * imports keep working and keep pointing at one definition.
 *
 * THE RULES
 * ---------
 *   Rule 1 — isOpenChat:     "does this still exist as work?"      → only Finish ends it
 *   Rule 3 — isOrphanedChat: "is this dead and unattended?"        → a subset of open
 *
 * Rule 2 (sub-session detection) lives in src/utils/subSession — same idea, same reason.
 *
 * An orphan is STILL OPEN. It stays visible and keeps its tab; it just must not paint
 * that tab orange forever. Hiding an orphan is not allowed: "a workspace with an
 * unfinished chat must be open" (Rafael, 2026-08-12).
 *
 * FAIL-FAST, AND WHERE IT BELONGS
 * -------------------------------
 * The parameter is deliberately NOT nullable. Passing a possibly-absent conversation
 * used to answer `false` — "not open" — which silently HIDES work, the precise failure
 * both incidents above are made of. Now it is a compile error at the call site, which
 * is the earliest and cheapest place to catch it, and costs nothing at runtime.
 *
 * What these functions deliberately do NOT do is throw on a malformed record. Measured
 * against live data on 2026-08-17: of 667 conversations, 649 carry a boolean and the 18
 * OPEN ones carry `manualFinished: null`. A strict shape check at the API boundary would
 * therefore have rejected exactly the open chats — the ones that must never disappear.
 * `null` is part of the contract and is typed as such below, rather than being asserted
 * away. (The no-throw doctrine for UI render paths is argued in
 * src/utils/chatLifecycle.ts:reportUnattributableOpenChats — a UI has no operator
 * standing by, so blanking it over a counting inconsistency loses more than it reports.)
 */

export interface LifecycleConv {
  sessionId?: string;
  projectId?: string;
  projectName?: string;
  /**
   * `null` is a legitimate value, not a defect: the server writes it for chats that were
   * never finished. Treating it as anything but "open" hides live work — see the header.
   */
  manualFinished?: boolean | null;
  liveness?: string;
  processAlive?: boolean;
}

/**
 * Rule 1 — "open": the chat exists until someone clicks Finish.
 *
 * Deliberately NOT influenced by processAlive or liveness. A dead process is not a
 * closed chat — it is an unfinished chat whose process died, and the user still has to
 * look at it. This is the rule behind the AC badge, /api/all-active-chats, the default
 * of GET /api/mission/conversations, and which sessions get a tab.
 */
export function isOpenChat(conv: LifecycleConv): boolean {
  return !conv.manualFinished;
}

/**
 * Rule 3 — "orphaned": dead process, idle for a long time, never finished.
 *
 * Used to decide URGENCY, never EXISTENCE. An orphan is open (Rule 1 still holds) and
 * must stay reachable; it simply does not demand attention right now. The server sets
 * liveness==='stuck' for this state.
 */
export function isOrphanedChat(conv: LifecycleConv): boolean {
  return isOpenChat(conv) && conv.liveness === 'stuck';
}
